import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FieldValue, FilterQuery, Franchise, InviteUser, Naics, Organization, OrgMemberUpdate, Promocode, RestResponse, User, Zip } from '../model';
import { ApiHeaders } from './headers';
import { Observable } from 'rxjs';
import {CampaignItemType} from '../model/campaign-item';

@Injectable()
export class AdministratorProvider {
    constructor(
        private http: HttpClient
    ) { }

    // #region "User"
    /**
     * @description also use for get org users
     * @param page
     * @param size
     * @param organizationId
     * @param query
     * @returns
     */
    getSystemUserList(page: number = 1, size: number = 10, organizationId: string = '', query: string = '', body: any = null): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/user/list?` +
            `page=${page}` +
            `&size=${size}` +
            `&organizationId=${organizationId}` +
            `&query=${query}`, body, ApiHeaders.getOptions());
    }

  /**
   * @description also use for get org users
   * @param page
   * @param size
   * @param organizationId
   * @param query
   * @returns
   */
  getExtendedSystemUserList(page: number = 1, size: number = 10, organizationId: string = '', query: string = '', body: any = null): Observable<RestResponse> {
    return this.http.post<RestResponse>(`/api/admin/user/list/extended?` +
      `page=${page}` +
      `&size=${size}` +
      `&organizationId=${organizationId}` +
      `&query=${query}`, body, ApiHeaders.getOptions());
  }

    /**
     @desc invite user
    */
    inviteUser(fv: FieldValue, type?: string, typeOther?: string): Observable<RestResponse> {
        return this.http.post<RestResponse>('/api/admin/user/invite' +
        (type ? `?type=${type}` : '') +
        (typeOther ? `&typeOther=${typeOther}` : ''), fv, ApiHeaders.getOptions());
    }

    /**
     @desc re-invite user
     */
    reInviteUser(userId: string): Observable<RestResponse> {
      return this.http.post<RestResponse>(`/api/admin/user/${userId}/invite`, null, ApiHeaders.getOptions());
    }

    /**
     @desc delete user
    */
    deleteUser(userId: string): Observable<RestResponse> {
        return this.http.delete<RestResponse>(`/api/admin/user/${userId}`, ApiHeaders.getOptions());
    }

    saveUserDetails(user: OrgMemberUpdate): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/user`, user, ApiHeaders.getOptions());
    }

    /**
     @desc delete user
     */
    getAdminUser(userId: string): Observable<RestResponse> {
      return this.http.get<RestResponse>(`/api/admin/user/${userId}`, ApiHeaders.getOptions());
    }
    // #endregion

    // #region "Zip"
    getZipList(page: number = 1, size: number = 10, query: string = ''): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/zip/list?` +
            `page=${page}` +
            `&size=${size}` +
            `&query=${query}`, ApiHeaders.getOptions());
    }
    /**
    * @desc delete zip
    */
    deleteZip(zipId: string): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/zip/${zipId}/delete`, null, ApiHeaders.getOptions());
    }
    /**
     * @desc get zip details
     */
    getZipDetails(zipCode: string): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/zip/val/${zipCode}`, ApiHeaders.getOptions());
    }
    /**
     * @desc save zip details
     */
    saveZipDetails(zip: Zip): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/zip`, zip, ApiHeaders.getOptions());
    }
    // #endregion

    // #region "Reports"
    getAllReportList(page: number = 1, size: number = 10, queryObj: FilterQuery): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/reports/list?` +
            `page=${page}` +
            `&size=${size}`, queryObj, ApiHeaders.getOptions());
    }

    copyReportAssignToMe(reportId: string): Observable<RestResponse> {
        return this.http.put<RestResponse>(`/api/admin/reports/assigntome/${reportId}`, ApiHeaders.getOptions());
    }
    // #endregion

    // #region "Promocodes APIs"
    getPromocodeList(page: number = 1, size: number = 10, query: string = ''): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/promocode/list?` +
            `page=${page}` +
            `&size=${size}` +
            `&query=${query}`, ApiHeaders.getOptions());
    }
    /**
    * @desc delete promocode
    */
    deletePromocode(promocodeId: string): Observable<RestResponse> {
        return this.http.delete<RestResponse>(`/api/admin/promocode/${promocodeId}/delete`, ApiHeaders.getOptions());
    }
    /**
     * @desc get promocode details
     */
    getPromocodeDetails(promocode: string): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/promocode/${promocode}`, ApiHeaders.getOptions());
    }
    /**
     * @desc save promocode details
     */
    savePromocodeDetails(promo: Promocode): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/promocode`, promo, ApiHeaders.getOptions());
    }
    // #endregion

    // #region "Organizations APIs"
    getOrganizationsList(page: number = 1, size: number = 10, query: string = ''): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/org/list?` +
            `page=${page}` +
            `&size=${size}` +
            `&query=${query}`, ApiHeaders.getOptions());
    }

    /**
     * @desc save organization details
     */
    saveOrganizationDetails(org: Organization): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/org`, org, ApiHeaders.getOptions());
    }

    /**
     * @desc get Organization details
     */
    getOrganizationDetails(orgId: string): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/org/${orgId}`, ApiHeaders.getOptions());
    }

    addUserInOrganization(orgId: string, obj: InviteUser): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/org/adduser/${orgId}`, obj, ApiHeaders.getOptions());
    }

    removeUserFromOrg(orgId: string, userId: string): Observable<RestResponse> {
        return this.http.delete<RestResponse>(`/api/admin/user/org/${orgId}/${userId}`, ApiHeaders.getOptions());
    }
    // #endregion

    // #region "Naics APIs"
    getNaicsList(page: number = 1, size: number = 10, query: string = ''): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/naics/list?` +
            `page=${page}` +
            `&size=${size}` +
            `&query=${query}`, ApiHeaders.getOptions());
    }

    /**
    * @desc delete Naics
    */
    deleteNaics(naicsCode: number): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/naics/${naicsCode}/delete`, null, ApiHeaders.getOptions());
    }
    /**
     * @desc get Naics details
     */
    getNaicsDetails(naicsCode: number): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/naics/${naicsCode}`, ApiHeaders.getOptions());
    }
    /**
     * @desc save Naics details
     */
    saveNaicsDetails(naics: Naics): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/naics`, naics, ApiHeaders.getOptions());
    }
    // #endregion

    // #region "Franchise"
    getFranchiseList(page: number = 1, size: number = 10, query: string = ''): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/franchise/list?` +
            `page=${page}` +
            `&size=${size}` +
            `&query=${query}`, ApiHeaders.getOptions());
    }
    /**
    * @desc delete Franchise
    */
    deleteFranchise(franchiseId: string): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/franchise/${franchiseId}/delete`, null, ApiHeaders.getOptions());
    }
    /**
     * @desc get Franchise details
     */
    getFranchiseDetails(franchiseId: string): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/franchise/${franchiseId}`, ApiHeaders.getOptions());
    }
    /**
     * @desc save Franchise details
     */
    saveFranchiseDetails(franchise: Franchise): Observable<RestResponse> {
        return this.http.post<RestResponse>(`/api/admin/franchise`, franchise, ApiHeaders.getOptions());
    }
    // #endregion

    // #region "Roles"
    getUserRoles(query: string, forOrg: boolean = false): Observable<RestResponse> {
        return this.http.get<RestResponse>(`/api/admin/role/list?` +
            `query=${query}` +
            `&availableForOrg=${forOrg}`, ApiHeaders.getOptions());
    }
    // #endregion

    getUserCampaignsList(id: string): Observable<RestResponse> {
      return this.http.get<RestResponse>(`/api/admin/campaign/user/${id}`, ApiHeaders.getOptions());
    }

    doCampaign(id: string, type: CampaignItemType, grace: string): Observable<RestResponse> {
      return this.http.post<RestResponse>(`/api/admin/campaign/user/${id}/send?type=${type}&grace=${grace}`, null, ApiHeaders.getOptions());
    }

  /**@desc delete user */
  testCampaign(): Observable<RestResponse> {
    return this.http.put<RestResponse>(`/api/admin/campaign/user/test`, ApiHeaders.getOptions());
  }
}
